import type { KeyboardEvent } from 'react';

/**
 * The WAI-ARIA tabs pattern for the app's segmented controls: each tab names
 * the panel it controls, the selected tab is the only one in the Tab order
 * (roving tabindex), and the arrow keys move the selection with the focus.
 * Three small helpers rather than a component, because the three tab strips
 * (post status, which posts, phone preview) share nothing visual.
 */

/** Spread on a `role="tablist"`: arrows, Home and End move the selection and the focus. */
export function tabListKeyDown<T extends string | number>(values: readonly T[], current: T, onChange: (next: T) => void) {
  return (event: KeyboardEvent<HTMLElement>) => {
    const index = values.indexOf(current);
    let next: T | undefined;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = values[(index + 1) % values.length];
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = values[(index - 1 + values.length) % values.length];
    else if (event.key === 'Home') next = values[0];
    else if (event.key === 'End') next = values[values.length - 1];
    if (next === undefined || next === current) return;
    event.preventDefault();
    onChange(next);
    event.currentTarget.querySelector<HTMLElement>(`[data-tab="${next}"]`)?.focus();
  };
}

/** Spread on each tab button. `idPrefix` names the tabs, `panelId` the one panel they switch. */
export function tabProps<T extends string>(value: T, current: T, idPrefix: string, panelId: string) {
  return {
    role: 'tab' as const,
    id: `${idPrefix}-${value}`,
    'data-tab': value,
    'aria-selected': value === current,
    'aria-controls': panelId,
    tabIndex: value === current ? 0 : -1,
  };
}

/** Spread on the panel: labelled by the selected tab, so its name follows the selection. */
export function tabPanelProps<T extends string>(current: T, idPrefix: string, panelId: string) {
  return { role: 'tabpanel' as const, id: panelId, 'aria-labelledby': `${idPrefix}-${current}` };
}
